/**
 * JNTU / Alphanumeric Roll Number Rank Calculator & Comparator
 * Enforces strict JNTU roll number ordering:
 * 1. Batch / Department Prefix (22N81A05...)
 * 2. Numeric Suffixes (01..99)
 * 3. Alphanumeric Suffixes (A0..A9, B0..B9, ..., Z0..Z9)
 * 4. Alphabetic Suffixes (AA..ZZ)
 * 5. Next Batch (23N81A05...)
 */
export const extractEnrollmentString = (s) => {
  if (!s) return "";
  if (typeof s === "string") return s.trim().toUpperCase();
  if (typeof s === "object") {
    return (
      s.enrollmentNo ||
      s.student?.enrollmentNo ||
      s.enrollment ||
      s.rollNo ||
      s.loginid ||
      s.studentId ||
      ""
    ).toString().trim().toUpperCase();
  }
  return String(s).trim().toUpperCase();
};

export const getJntuRank = (s) => {
  const str = extractEnrollmentString(s);
  if (!str) return { prefix: "", rank: 0, raw: "" };
  if (str.length < 3) return { prefix: str, rank: 0, raw: str };

  // For standard JNTU numbers (e.g., 22N81A0501, 22N81A05A0, 22N81A05B0)
  const prefix = str.substring(0, str.length - 2);
  const suff = str.substring(str.length - 2);

  // Pure 2-digit numeric suffix (00..99) -> Rank 0..99
  if (/^\d{2}$/.test(suff)) {
    return { prefix, rank: parseInt(suff, 10), raw: str };
  }

  // Alphanumeric suffix (A0..Z9) -> Rank 100..359
  // A0=100, A9=109, B0=110, B9=119, C0=120...
  if (/^[A-Z]\d$/.test(suff)) {
    const charCode = suff.charCodeAt(0) - 65; // A=0, B=1, ... Z=25
    const digit = parseInt(suff[1], 10);
    return { prefix, rank: 100 + charCode * 10 + digit, raw: str };
  }

  // Pure alpha 2-char suffix (AA..ZZ) -> Rank 400..1075
  if (/^[A-Z]{2}$/.test(suff)) {
    const charCode1 = suff.charCodeAt(0) - 65;
    const charCode2 = suff.charCodeAt(1) - 65;
    return { prefix, rank: 400 + charCode1 * 26 + charCode2, raw: str };
  }

  // Fallback rank based on character code sum
  return { prefix, rank: 2000, raw: str };
};

export const sortEnrollmentNo = (a, b) => {
  const rA = getJntuRank(a);
  const rB = getJntuRank(b);

  // First compare prefixes (e.g. 22N81A05 vs 22N81A12)
  if (rA.prefix !== rB.prefix) {
    return rA.prefix.localeCompare(rB.prefix, undefined, { numeric: true, sensitivity: "base" });
  }

  // If ranks differ within the same prefix, sort by rank (01..99, then A0..A9, B0..B9 etc.)
  if (rA.rank !== rB.rank) {
    return rA.rank - rB.rank;
  }

  // Final fallback: natural string comparison
  return (rA.raw || "").localeCompare(rB.raw || "", undefined, { numeric: true, sensitivity: "base" });
};

export default sortEnrollmentNo;
